import { Injectable } from '@angular/core';
import { ProjectInfo, ProjectInfoResponse, ProjectProperty, ProjectPropertyConfig } from '../models/project-info.types';
import { PropertyValueWithUnit } from '../../../core/models/abacus-api.types';

@Injectable({ providedIn: 'root' })
export class ProjectInfoMapper {
  private readonly propertyRegistry = new Map<keyof ProjectInfoResponse, ProjectPropertyConfig>([
    // Category
    ['PSC_Category__c', { label: 'Category' }],

    // Country
    ['PSC_Country__c', { label: 'Country' }],

    // Booking period
    ['PSC_Start_Date__c', { label: 'Start date' }],
    ['PSC_End_Date__c', { label: 'End date' }],

    // Air
    ['PSC_Peak_FAD__c', { label: 'Peak load' }],
    ['PSC_Working_Pressure__c', { label: 'Working pressure' }],
    ['PSC_Oil_Free_Air__c', { label: '100% Oil free air' }],
    ['PSC_Dry_Air__c', { label: 'Dry air' }],
    ['PSC_PDP__c', { label: 'Pressure dew point' }],

    // Power
    ['PSC_Altitude__c', { label: 'Altitude' }],
    ['PSC_Temperature__c', { label: 'Temperature' }],
    ['PSC_Peak_Load__c', { label: 'Peak load' }],
    ['PSC_Frequency__c', { label: 'Frequency' }],
    ['PSC_Voltage__c', { label: 'Voltage' }],

    // Steam
    ['PSC_Water_Quality_Type__c', { label: 'Water quality type' }],
    ['PSC_Condensate_return__c', { label: 'Condensate return' }],
    ['PSC_Pressure__c', { label: 'Pressure' }],
    ['PSC_Max_Steam_Consumption__c', { label: 'Max steam consumption' }],
    ['PSC_Water_Inlet_Temp__c', { label: 'Water inlet temperature' }],
    ['PSC_Drains__c', { label: 'Drains' }],
    ['PSC_Condensate_Temperature__c', { label: 'Condensate temperature' }],
    ['PSC_Condensate__c', { label: 'Condensate' }],
  ]);

  mapToProjectInfo(response: ProjectInfoResponse): ProjectInfo {
    const startDate = new Date(response.PSC_Start_Date__c);
    const endDate = new Date(response.PSC_End_Date__c);

    return {
      id: response.Id,
      name: response.PSC_Project_Name__c?.trim() ? response.PSC_Project_Name__c : 'Project details',
      description: response.PSC_Project_Description__c ?? null,
      country: response.PSC_Country__c,
      category: response.PSC_Category__c,
      projectPeriod: {
        start: startDate,
        end: endDate,
        formatted: this.calculateDuration(startDate, endDate),
      },
      contact: {
        name: response.contact_details.Name,
        phone: response.contact_details.Phone,
        address: response.contact_details.Address,
      },
      properties: this.extractProperties(response),
    };
  }

  private extractProperties(response: ProjectInfoResponse): ProjectProperty[] {
    const properties: ProjectProperty[] = [];

    for (const [key, config] of this.propertyRegistry) {
      const rawValue = response[key];
      const property = this.normalizeValue(rawValue, config);
      if (property) {
        properties.push(property);
      }
    }

    return properties;
  }

  private normalizeValue(value: unknown, config: ProjectPropertyConfig): ProjectProperty | null {
    if (value === null || value === undefined) {
      return null;
    }

    // PropertyValueWithUnit
    if (this.isPropertyValueWithUnit(value)) {
      if (value.value === null) {
        return null;
      }
      return {
        label: config.label,
        value: value.value,
        unit: value.unit ?? undefined,
      };
    }

    // Boolean
    if (typeof value === 'boolean') {
      return {
        label: config.label,
        value: value ? 'Yes' : 'No',
      };
    }

    // String or number
    if (typeof value === 'string' || typeof value === 'number') {
      return {
        label: config.label,
        value,
      };
    }

    return null;
  }

  private isPropertyValueWithUnit(value: unknown): value is PropertyValueWithUnit<number | null, string | null> {
    return typeof value === 'object' && value !== null && 'value' in value && 'unit' in value;
  }

  private calculateDuration(start: Date, end: Date): string {
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    const weekStr = weeks > 0 ? `${weeks} week${weeks > 1 ? 's' : ''}` : '';
    const dayStr = days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '';

    if (weekStr && dayStr) {
      return `${weekStr}, ${dayStr}`;
    }
    return weekStr || dayStr || '0 days';
  }
}
